import axios, { AxiosResponse } from "axios";
import { ConferenceApplicationDetailsResponse } from "../interfaces/conference-applications/ConferenceApplicationsDetailsResponse";
import { ConferenceApplicationResponse } from "../interfaces/conference-applications/ConferenceApplicationResponse";
import { ConfirmConferenceApplicationDeletionResponse } from "../interfaces/conference-applications/ConfirmConferenceApplicationDeletionResponse";
import { SetIsFeaturedOnParticipantsListRequest } from "../interfaces/conference-applications/SetIsFeaturedOnParticipantsListRequest";
import { getAuthenticationHeaders } from "../utils/authenticationsUtils";

const CONFERENCE_APPLICATIONS_API_URL: string = `${process.env.REACT_APP_API_URL}/conference-applications`;

const applyToConference = (
  formData: FormData
): Promise<AxiosResponse<any, any>> => {
  return axios.post(`${CONFERENCE_APPLICATIONS_API_URL}/create`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

const getAllConferenceApplications = async (): Promise<
  ConferenceApplicationResponse[]
> => {
  const getAllConferenceApplicationsResponse = await axios.get(
    `${CONFERENCE_APPLICATIONS_API_URL}/all`
  );
  return getAllConferenceApplicationsResponse.data;
};

const getAllConferenceApplicationsWithDeleted = async (): Promise<
  ConferenceApplicationDetailsResponse[]
> => {
  const getAllConferenceApplicationsWithDeletedResponse = await axios.get(
    `${CONFERENCE_APPLICATIONS_API_URL}/all-with-deleted`
  );
  return getAllConferenceApplicationsWithDeletedResponse.data;
};

const deleteConferenceApplication = async (
  id: string
): Promise<ConferenceApplicationDetailsResponse> => {
  const headers = getAuthenticationHeaders();

  const deleteConferenceApplicationResponse = await axios.delete(
    `${CONFERENCE_APPLICATIONS_API_URL}/delete/${id}`,
    { headers }
  );
  
  return deleteConferenceApplicationResponse.data;
};

const confirmConferenceApplicationDeletion = async (
  id: string
): Promise<ConfirmConferenceApplicationDeletionResponse> => {
  const headers = getAuthenticationHeaders();

  const confirmConferenceApplicationDeletionResponse = await axios.delete(
    `${CONFERENCE_APPLICATIONS_API_URL}/confirm-deletion/${id}`,
    { headers }
  );

  return confirmConferenceApplicationDeletionResponse.data;
};

const restoreConferenceApplication = async (
  id: string
): Promise<ConferenceApplicationDetailsResponse> => {
  const headers = getAuthenticationHeaders();

  const restoreConferenceApplicationResponse = await axios.post(
    `${CONFERENCE_APPLICATIONS_API_URL}/restore/${id}`,
    {},
    { headers }
  );
  
  return restoreConferenceApplicationResponse.data;
};

const setIsFeaturedOnParticipantsList = async (
  id: string,
  setIsFeaturedOnParticipantsListRequest: SetIsFeaturedOnParticipantsListRequest
): Promise<ConferenceApplicationResponse> => {
  const headers = getAuthenticationHeaders();

  const setIsFeaturedOnParticipantsListResponse = await axios.put(
    `${CONFERENCE_APPLICATIONS_API_URL}/set-is-featured-on-participants-list/${id}`,
    setIsFeaturedOnParticipantsListRequest,
    { headers }
  );

  return setIsFeaturedOnParticipantsListResponse.data;
};

const getAbstractFiles = async (id: string): Promise<string[]> => {
  const getAbstractFilesResponse = await axios.get(`${CONFERENCE_APPLICATIONS_API_URL}/get-abstract-files/${id}`);
  return getAbstractFilesResponse.data;
};

const downloadAbstractFile = async (
  id: string,
  abstractFileName: string
): Promise<Blob> => {
  const downloadAbstractFileResponse = await axios.get(
    `${CONFERENCE_APPLICATIONS_API_URL}/download-abstract-file/${id}/${abstractFileName}`,
    {
      responseType: "blob",
    }
  );

  return downloadAbstractFileResponse.data;
};

const downloadAllSavedAbstractFilesAsZipArchive = async (): Promise<Blob> => {
  const headers = getAuthenticationHeaders();

  const downloadAllSavedAbstractFilesAsZipArchiveResponse = await axios.get(
    `${CONFERENCE_APPLICATIONS_API_URL}/download-all-saved-abstracts-files-as-zip-archive`,
    {
      headers,
      responseType: "blob",
    }
  );

  return downloadAllSavedAbstractFilesAsZipArchiveResponse.data;
};

const conferenceApplicationsService = {
  applyToConference,
  getAllConferenceApplications,
  getAllConferenceApplicationsWithDeleted,
  deleteConferenceApplication,
  confirmConferenceApplicationDeletion,
  restoreConferenceApplication,
  setIsFeaturedOnParticipantsList,
  getAbstractFiles,
  downloadAbstractFile,
  downloadAllSavedAbstractFilesAsZipArchive,
};

export default conferenceApplicationsService;